<template>
  <div>
    <v-data-table-server
      v-model:options="pagination"
      :headers="headers"
      :items="orders"
      :items-length="totalItems"
      :loading="loading"
      class="elevation-1 nowrap-headers"
      :items-per-page="pagination.itemsPerPage"
      :items-per-page-options="[
        { value: 5, title: '5' },
        { value: 10, title: '10' },
        { value: 25, title: '25' },
        { value: 50, title: '50' },
      ]"
      items-per-page-text="Rows per page"
      @update:options="updatePagination"
    >
      <template #progress>
        <v-progress-linear color="red" indeterminate />
      </template>
      <template #item="{ item, index }">
        <tr
          :class="{ tableRowColorSelected: index === activeRow }"
          class="tableRowColor"
          @click="activeRow = index"
        >
          <!----------------------------------------STATUS----------------------------------------->
          <td v-if="item.status === 1">
            <v-chip label variant="outlined" color="success">
              {{ $t("leko_employees.shippings.table.confirmed") }}
            </v-chip>
          </td>
          <td v-else-if="item.status === 2">
            <v-chip label variant="outlined" color="success">
              {{ $t("leko_employees.shippings.table.verified") }}
            </v-chip>
          </td>
          <td v-else-if="item.status === 3">
            <v-chip label variant="outlined" color="primary">
              {{ $t("leko_employees.shippings.table.shipped") }}
            </v-chip>
          </td>
          <td v-else-if="item.status === 4">
            <v-chip label variant="outlined" color="error">
              {{ $t("leko_employees.shippings.table.blocked") }}
            </v-chip>
          </td>

          <td v-else-if="item.status === 6">
            <v-chip label variant="outlined" color="amber">
              {{ $t("leko_employees.shippings.table.return_confirmed") }}
            </v-chip>
          </td>
          <td v-else-if="item.status === 7">
            <v-chip label variant="outlined" color="primary">
              {{ $t("leko_employees.shippings.table.returned") }}
            </v-chip>
          </td>
          <!------------------------------------------ID------------------------------------------->
          <td v-if="item.part_id.old_id && item.part_id.old_id !== undefined">
            <v-row
              class="pointer ma-1 align-center justify-center d-flex flex-column"
              @click="
                openQrDialog({
                  id: item.part_id.old_id,
                  type: 'shipping',
                  car_id: item.part_id.car_id,
                  printed: item.part_id.printed,
                })
              "
            >
              <qr-code :size="60" :text="item.part_id.old_id" />
              {{
                item.part_id.old_id.slice(
                  item.part_id.old_id.length - 6,
                  item.part_id.old_id.length,
                )
              }}
            </v-row>
          </td>
          <td v-else class="text-xs">
            <v-row
              class="pointer ma-1 align-center justify-center d-flex flex-column"
              @click.stop="
                openQrDialog({
                  id: item.part_id._id,
                  type: 'shipping',
                  car: item.part_id.car_id,
                  printed: item.part_id.printed,
                })
              "
            >
              <qr-code :size="60" :text="item.part_id._id" />
              {{
                item.part_id._id.slice(
                  item.part_id._id.length - 4,
                  item.part_id._id.length,
                )
              }}
            </v-row>
          </td>
          <!-----------------------------------------GROUP----------------------------------------->
          <td>
            <v-row class="d-flex flex-column">
              <span v-if="item.part_id.group" class="text-xs font-weight-bold">
                {{ multilanguage(item.part_id.group.description) }}
              </span>
              <span v-if="item.part_id.group" class="text-xs font-italic">
                {{ multilanguage(item.part_id.group.subgroup.description) }}
              </span>
            </v-row>
          </td>
          <!-----------------------------------------TITLE----------------------------------------->
          <td class="text-xs">
            <v-row class="d-flex flex-column">
              <span v-if="item.part_id.title" class="text-xs font-weight-bold">
                {{ multilanguage(item.part_id.title) }}
              </span>
              <span v-if="item.part_id.group" class="text-xs font-italic">
                {{
                  positions(
                    item.part_id.group.item.positions.x,
                    item.part_id.group.item.positions.y,
                  )
                }}
              </span>
            </v-row>
          </td>
          <!-----------------------------------MANUFACTURER CODE----------------------------------->
          <td class="text-xs">
            {{ item.part_id.manufacturer_code }}
          </td>
          <!----------------------------------------IMAGES----------------------------------------->
          <td>
            <v-row class="ma-1">
              <p style="display: none">
                {{ item.part_id.old_id }}
              </p>
              <img
                :src="`${server}/images?q=${item.part_id.images[0].image}`"
                class="pointer"
                height="75"
                width="100"
                @click="showImages(item.part_id._id, item.part_id.images)"
              />
            </v-row>
          </td>
          <!-----------------------------------------NOTES----------------------------------------->
          <td class="text-xs">
            <v-row
              align="center"
              justify="center"
              class="pointer py-3"
              @click="showNotes(item.part_id.notes)"
            >
              <span
                v-if="
                  item.part_id &&
                  item.part_id.notes &&
                  item.part_id.notes.length > 40
                "
              >
                {{ item.part_id.notes.substring(0, 40) }}...
                <v-icon>mdi-eye</v-icon
                >{{
                  item.part_id.notes
                    ? "(" + item.part_id.notes.length + ")"
                    : ""
                }}
              </span>
              <span v-else
                >{{ item.part_id.notes
                }}{{
                  item.part_id.notes
                    ? "(" + item.part_id.notes.length + ")"
                    : ""
                }}</span
              >
            </v-row>
          </td>
          <!----------------------------------------QUALITY---------------------------------------->
          <td v-if="item.part_id.quality">
            <div class="text-truncate">
              <v-rating
                :model-value="item.part_id.quality.rating"
                color="red"
                background-color="red-darken-4"
                readonly
                :size="14"
              />
            </div>
          </td>
          <td v-else class="text-xs" />
          <!---------------------------------------LOCATION---------------------------------------->
          <td v-if="item.part_id && item.part_id.shelf_data" class="text-xs">
            <span>
              {{ item.part_id.shelf_data.shelf.room_name }} -
              {{ item.part_id.shelf_data.shelf.location_code }}:&nbsp;
              {{ item.part_id.shelf_data.shelf.x }},
              {{ item.part_id.shelf_data.shelf.y }}
            </span>

            <div class="text-no-wrap">
              <span class="caption">Mag: </span>
              <span class="font-weight-bold caption">{{
                item.part_id.shelf_data.repo
              }}</span>
            </div>
          </td>
          <td v-else class="text-xs">-</td>
          <!-----------------------------------------PRICE----------------------------------------->
          <td class="text-xs">
            <v-chip @click="openPriceDialog(item.price)">
              {{ item.price.logistic_price }} {{ item.part_id.currency }}
            </v-chip>
          </td>
          <!-------------------------------------DATE CREATED-------------------------------------->
          <td>
            <v-btn variant="outlined">
              {{ formatDate(item.part_id.date_created) }}
            </v-btn>
          </td>
          <!--------------------------------------ORDER DATE--------------------------------------->
          <td>
            <v-btn variant="outlined" color="green-darken-2">
              {{ formatDate(item.date_created) }}
            </v-btn>
          </td>
          <!----------------------------------------ACTIONS---------------------------------------->
          <td>
            <v-row class="d-flex flex-nowrap">
              <p style="display: none">
                {{ item.part_id._id }}
              </p>
              <v-btn
                icon
                variant="text"
                @click="
                  showBuyerDetails({
                    id: item._id,
                    buyerDetails: item.buyer_details,
                  })
                "
              >
                <v-icon color="grey-darken-2"> mdi-account-box </v-icon>
              </v-btn>
              <v-btn
                :disabled="item.status === 2 || item.status === 3"
                icon
                variant="text"
                @click="openActionsDialog(item)"
              >
                <v-icon color="grey-darken-2"> mdi-compare-horizontal </v-icon>
              </v-btn>
              <v-btn
                :disabled="item.status !== 2"
                icon
                variant="text"
                @click="shipped(item)"
              >
                <v-icon color="grey-darken-2"> mdi-check </v-icon>
              </v-btn>
            </v-row>
          </td>
          <!--------------------------------------------------------------------------------------->
        </tr>
      </template>
    </v-data-table-server>
  </div>
</template>

<script>
import moment from "moment";

import VueQrCode from "vue-qr-generator";

import getRequest from "../../api/getRequest";
import putRequest from "../../api/putRequest";

export default {
  components: {
    "qr-code": VueQrCode,
  },
  data: () => ({
    loading: false,
    activeRow: null,
    orders: [],
    totalItems: 0,
    pagination: {
      itemsPerPage: 5,
      page: 1,
      groupBy: ["date_created"],
      sortBy: [{ key: "date_created", order: "desc" }],
      sortDesc: [true],
    },
    headers: [
      {
        title: "Status",
        sortable: true,
        key: "status",
      },
      {
        title: "ID",
        sortable: true,
        key: "_id",
      },
      {
        title: "Group",
        sortable: true,
        key: "group",
      },
      {
        title: "Title",
        sortable: true,
        key: "title",
      },
      {
        title: "Manufacturer Code",
        sortable: false,
        key: "manufacturer_code",
      },
      {
        title: "Images",
        sortable: false,
        key: "old_id",
      },

      {
        title: "Notes",
        sortable: false,
        key: "notes",
      },
      {
        title: "Quality",
        sortable: false,
        key: "quality",
      },
      {
        title: "Room",
        sortable: false,
        key: "shelf_data.shelf.room_name",
      },
      {
        title: "Price",
        sortable: true,
        key: "price",
      },
      {
        title: "Date Created",
        sortable: false,
        key: "part._id.date_created",
      },
      {
        title: "Order Date",
        sortable: true,
        key: "date_created",
      },
      {
        title: "Actions",
        sortable: false,
        key: "_id",
      },
    ],
  }),
  computed: {
    server() {
      return this.$serverUri;
    },
  },
  watch: {
    pagination: {
      handler() {
        this.getOrders();
      },
      deep: true,
    },
    orders(value) {
      this.$emit("items", value);
    },
  },
  beforeMount() {
    this.getOrders();
    this.getLocale();
  },
  methods: {
    getLocale() {
      this.headers[0].title = this.$t("leko_employees.shippings.table.status");
      this.headers[2].title = this.$t("leko_employees.shippings.table.group");
      this.headers[3].title = this.$t("leko_employees.shippings.table.title");
      this.headers[4].title = this.$t(
        "leko_employees.shippings.table.manufacturer_code",
      );
      this.headers[5].title = this.$t("leko_employees.shippings.table.images");
      this.headers[6].title = this.$t("leko_employees.shippings.table.notes");
      this.headers[7].title = this.$t("leko_employees.shippings.table.quality");
      this.headers[8].title = this.$t(
        "leko_employees.shippings.table.location",
      );
      this.headers[9].title = this.$t("leko_employees.shippings.table.price");
      this.headers[10].title = this.$t(
        "leko_employees.shippings.table.date_created",
      );
      this.headers[11].title = this.$t(
        "leko_employees.shippings.table.order_date",
      );
      this.headers[12].title = this.$t(
        "leko_employees.shippings.table.actions",
      );
    },
    getOrders(status) {
      this.loading = true;
      const sort = this.pagination.sortBy?.[0] || {
        key: "date_created",
        order: "desc",
      };
      getRequest(
        "/order/shipping",
        `institution_id=${this.$store.getters.institutionId}&role=${
          this.$store.getters.role
        }&limit=${this.pagination.itemsPerPage}&page=${
          this.pagination.page
        }&sort_by=${sort.key}&descending=${
          sort.order === "desc"
        }&status=${status}`,
      )
        .then((response) => {
          this.orders = response.orders;
          this.totalItems = response.count;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    openQrDialog(data) {
      this.$emit("qr", data);
    },
    openPriceDialog(data) {
      this.$emit("price", data);
    },
    showBuyerDetails(data) {
      this.$emit("buyer-details", data);
    },
    showImages(id, images) {
      this.$emit("images", { type: "string", id, images });
    },
    shipped(data) {
      const orderId = data._id;
      putRequest("/order/shipped", `order_id=${orderId}`)
        .then(() => {
          this.getOrders();
        })
        .catch(() => {});
    },
    openActionsDialog(data) {
      this.$emit("actions", data);
    },
    updatePagination(options) {
      this.pagination = {
        ...options,
        itemsPerPage: options.itemsPerPage ? options.itemsPerPage : 5,
        groupBy:
          options.groupBy && options.groupBy.length
            ? options.groupBy
            : ["date_created"],
        sortBy:
          options.sortBy && options.sortBy.length
            ? options.sortBy
            : { key: "date_created", order: "desc" },
        sortDesc:
          options.sortDesc && options.sortDesc.length
            ? options.sortDesc
            : [true],
      };
      this.activeRow = null;
    },
    multilanguage(textObject) {
      switch (this.$store.getters.language) {
        case "en":
          return textObject.en;
        case "it":
          return textObject.it;
        case "sq":
          return textObject.al;
        default:
          return "";
      }
    },
    positions(x, y) {
      let posX = "";
      let posY = "";
      switch (this.$store.getters.language) {
        case "en":
          if (x === "sx.") {
            posX = "left";
          } else if (x === "dx.") {
            posX = "right";
          } else {
            posX = "";
          }
          if (y === "post.") {
            posY = "back";
          } else if (y === "ant.") {
            posY = "front";
          } else {
            posY = "";
          }
          if (posX.length > 0 || posY.length > 0) {
            return `${posX}, ${posY}`;
          }
          return "";
        case "it":
          if (x === "sx.") {
            posX = "sinistra";
          } else if (x === "dx.") {
            posX = "destra";
          } else {
            posX = "";
          }
          if (y === "post.") {
            posY = "posteriore";
          } else if (y === "ant.") {
            posY = "anteriore";
          } else {
            posY = "";
          }
          if (posX.length > 0 || posY.length > 0) {
            return `${posX}, ${posY}`;
          }
          return "";
        case "sq":
          if (x === "sx.") {
            posX = "majtas";
          } else if (x === "dx.") {
            posX = "djathtas";
          } else {
            posX = "";
          }
          if (y === "post.") {
            posY = "mbrapa";
          } else if (y === "ant.") {
            posY = "para";
          } else {
            posY = "";
          }
          if (posX.length > 0 || posY.length > 0) {
            return `${posX}, ${posY}`;
          }
          return "";
        default:
          return "";
      }
    },
    showNotes(notes) {
      this.$emit("show-notes", notes);
    },
    formatDate(date) {
      if (!date) return "";
      moment.locale(localStorage.getItem("language") || "en");
      return moment(date).format("LLL");
    },
  },
};
</script>
